"use client";

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MoveHorizontal, ArrowRight, Quote, Sparkles, CircleCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from '@/components/common/Link';
export default function TheJargonTranslator() {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLElement>(null);
  const handleMove = useCallback((clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = x / rect.width * 100;
    setSliderPosition(Math.min(100, Math.max(0, percentage)));
  }, []);
  const onMouseDown = () => setIsDragging(true);
  const onTouchStart = () => setIsDragging(true);
  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        e.preventDefault();
        handleMove(e.clientX);
      }
    };
    const handleGlobalTouchMove = (e: TouchEvent) => {
      if (isDragging) {
        handleMove(e.touches[0].clientX);
      }
    };
    const handleGlobalUp = () => setIsDragging(false);
    if (isDragging) {
      window.addEventListener('mousemove', handleGlobalMouseMove);
      window.addEventListener('mouseup', handleGlobalUp);
      window.addEventListener('touchmove', handleGlobalTouchMove);
      window.addEventListener('touchend', handleGlobalUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleGlobalMouseMove);
      window.removeEventListener('mouseup', handleGlobalUp);
      window.removeEventListener('touchmove', handleGlobalTouchMove);
      window.removeEventListener('touchend', handleGlobalUp);
    };
  }, [isDragging, handleMove]);
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      setSliderPosition(prev => Math.max(0, prev - 5));
    } else if (e.key === 'ArrowRight') {
      setSliderPosition(prev => Math.min(100, prev + 5));
    }
  };
  return <section id="the-jargon-translator" ref={containerRef} className="dark bg-background text-foreground relative w-full min-h-[700px] lg:min-h-[800px] overflow-hidden flex flex-col select-none group" aria-label="Jargon Translator Comparison">
            {}
            <div className="absolute inset-0 w-full h-full bg-[hsl(40_20%_98%)] flex items-center justify-center z-0">
                <div className="container mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center h-full">
                    {}
                    <div className="col-span-1 lg:col-span-2 flex flex-col items-center text-center max-w-4xl mx-auto">
                        <div className="mb-6 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[hsl(174_42%_95%)] text-[hsl(174_42%_25%)] text-sm font-medium">
                            <Sparkles className="w-4 h-4" />
                            <span>The KaleighWrites Approach</span>
                        </div>
                        
                        <h2 className="text-4xl md:text-6xl lg:text-7xl font-sans font-bold text-[hsl(220_25%_12%)] tracking-tight mb-8 leading-[1.1]">
                            We help you <br className="hidden md:block" />
                            <span className="text-[hsl(174_42%_45%)]">grow faster.</span>
                        </h2>
                        
                        <p className="text-lg md:text-xl text-[hsl(220_15%_40%)] max-w-2xl mb-10 leading-relaxed">
                            Clear, actionable messaging that drives real results. We strip away the complexity to reveal the value of your business, connecting you directly with the people who need you most.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4">
                            <Button asChild size="lg" className="bg-[hsl(220_25%_12%)] text-[hsl(40_20%_98%)] hover:bg-[hsl(220_25%_12%)]/90 border-none shadow-xl">
                                <Link to="/contact">
                                    Get Clear Copy <ArrowRight className="ml-2 w-4 h-4" />
                                </Link>
                            </Button>
                            <Button asChild variant="outline" size="lg" className="border-[hsl(220_10%_88%)] text-[hsl(220_25%_12%)] hover:bg-[hsl(220_10%_88%)]/50 bg-transparent">
                                <Link to="/services">
                                    View Services
                                </Link>
                            </Button>
                        </div>

                        <div className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-8 text-[hsl(220_25%_12%)]/80 text-sm font-medium">
                            <div className="flex items-center gap-2">
                                <CircleCheck className="w-5 h-5 text-[hsl(174_42%_45%)]" />
                                <span>Audience-Focused</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <CircleCheck className="w-5 h-5 text-[hsl(174_42%_45%)]" />
                                <span>Conversion-Driven</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <CircleCheck className="w-5 h-5 text-[hsl(174_42%_45%)]" />
                                <span>Jargon-Free</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {}
            <div className="absolute inset-y-0 left-0 z-10 overflow-hidden bg-background border-r border-white/10 shadow-[10px_0_50px_rgba(0,0,0,0.5)]" style={{
      width: `${sliderPosition}%`
    }}>
                {}
                <div className="absolute inset-0 w-full h-full flex items-center justify-center">
                    {}
                    <div className="w-screen max-w-full h-full flex items-center justify-center relative" style={{
          width: containerRef.current ? containerRef.current.offsetWidth : '100vw'
        }}>
                        {}
                        <div className="absolute inset-0 opacity-5 pointer-events-none overflow-hidden select-none">
                            <div className="absolute top-10 left-10 text-6xl font-serif italic whitespace-nowrap">Synergy</div>
                            <div className="absolute top-1/4 right-20 text-8xl font-serif italic whitespace-nowrap">Paradigm</div>
                            <div className="absolute bottom-20 left-1/3 text-7xl font-serif italic whitespace-nowrap">Holistic</div>
                            <div className="absolute top-1/3 left-20 text-5xl font-serif italic whitespace-nowrap">Bandwidth</div>
                            <div className="absolute bottom-1/4 right-10 text-6xl font-serif italic whitespace-nowrap">Deliverables</div>
                        </div>

                        <div className="container mx-auto px-6 md:px-12 flex flex-col items-center text-center max-w-4xl z-10">
                            <div className="mb-6 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-white/70 text-sm font-medium backdrop-blur-sm border border-white/5">
                                <Quote className="w-4 h-4" />
                                <span>The Industry Standard</span>
                            </div>

                            <h2 className="text-4xl md:text-6xl lg:text-7xl font-serif italic text-muted-foreground/60 tracking-tight mb-8 leading-[1.1]">
                                Leveraging synergistic <br className="hidden md:block" />
                                <span className="text-muted-foreground/40 decoration-wavy underline decoration-white/10 underline-offset-8">paradigms.</span>
                            </h2>
                            
                            <p className="text-lg md:text-xl text-muted-foreground/50 max-w-2xl mb-10 leading-relaxed font-serif italic">
                                Utilizing a holistic approach to optimize cross-functional deliverables and drill down into granular data points. We circle back on low-hanging fruit to ensure scalable bandwidth and disruptive innovation within the ecosystem.
                            </p>

                            {}
                            <div className="flex flex-col sm:flex-row gap-4 opacity-50 pointer-events-none grayscale">
                                <div className="h-11 px-8 rounded-md bg-white/10 border border-white/10 flex items-center justify-center text-white/40 font-serif italic">
                                    Initiate Optimization
                                </div>
                                <div className="h-11 px-8 rounded-md border border-white/10 flex items-center justify-center text-white/30 font-serif italic">
                                    View Deliverables
                                </div>
                            </div>

                            <div className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-8 text-muted-foreground/30 text-sm font-serif italic">
                                <div className="flex items-center gap-2 justify-center">
                                    <span>• Cross-Functional</span>
                                </div>
                                <div className="flex items-center gap-2 justify-center">
                                    <span>• Value-Added</span>
                                </div>
                                <div className="flex items-center gap-2 justify-center">
                                    <span>• Blue-Sky Thinking</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                {}
            </div>

            {}
            <div className="absolute inset-y-0 z-30 cursor-ew-resize touch-none" style={{
      left: `${sliderPosition}%`
    }} onMouseDown={onMouseDown} onTouchStart={onTouchStart} role="slider" aria-valuenow={sliderPosition} aria-valuemin={0} aria-valuemax={100} aria-label="Drag to translate jargon to clear text" tabIndex={0} onKeyDown={handleKeyDown}>
                {}
                <div className="absolute inset-y-0 -left-px w-0.5 bg-white/20 shadow-[0_0_10px_rgba(0,0,0,0.5)]"></div>
                
                {}
                <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-12 h-12 bg-[hsl(174_42%_45%)] rounded-full flex items-center justify-center shadow-2xl border-4 border-background text-white transition-transform hover:scale-110 active:scale-95">
                    <MoveHorizontal className="w-6 h-6" />
                </div>

                {}
                <div className="absolute inset-y-0 -left-8 -right-8 bg-transparent"></div>
                
                {}
                <div className={`absolute top-[45%] -translate-y-full -translate-x-1/2 mb-4 whitespace-nowrap pointer-events-none transition-opacity duration-300 ${isDragging ? 'opacity-0' : 'opacity-100'}`}>
                    <span className="bg-black/80 text-white text-xs px-2 py-1 rounded backdrop-blur-md">
                        Drag to Translate
                    </span>
                </div>
            </div>

            {}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 md:hidden pointer-events-none opacity-70 animate-pulse">
                <div className="flex items-center gap-2 text-xs text-white/80 bg-black/50 px-3 py-1 rounded-full backdrop-blur-sm">
                    <MoveHorizontal className="w-3 h-3" />
                    <span>Swipe to translate</span>
                </div>
            </div>

        </section>;
}